// src/components/dashboard/pages/WeighbridgeTicketList/index.jsx
import React from 'react';
import DataTable from 'react-data-table-component';
import {
  Truck,
  Eye,
  Pen,
  User,
  MapPin,
  Download,
  Calendar,
  FileSpreadsheet,
  InfoIcon,
  Printer,
  Mail,
} from 'lucide-react';
import i18n from '../../../../i18n';
import { SideBar } from '../../sidebar';
import NoData from '../../../common/NoData';
import { TableSkeleton } from '../../../common/skelton/TableSkelton';
import StatusTabs from '../../../common/StatusTab';
import HeaderBanner from '../../../common/HeaderBanner';
import {
  getWeighbridgeTicketList,
  exportWeighbridgeTickets,
  sendTransitReport,
  getDeliverySiteSelectors,
} from './services/WeighbridgeTicketService';
import { tableStyles } from '../../../../styles/components/js/tableStyle';
import { Link } from 'react-router-dom';
import FilterSection from '../../../common/Filter';
import { toast } from 'react-toastify';
import {
  getGroupsSelectors,
  getOrganizationSelectors,
} from '../LoadingManagement/services/LoadingManagementService';
import html2pdf from 'html2pdf.js';
import ForwardedWeightTicketPrint from '../LoadingManagement/components/WeightTicketPrint';

export class WeighbridgeTicketList extends React.Component {
  constructor() {
    super();
    this.printRef = React.createRef();
    const today = new Date().toISOString().split('T')[0];

    this.state = {
      search: {
        vehicle_id: '',
        delivery_site_id: '',
        status: '',
        driver_id: '',
        ticket_number: '',
        date: today,
      },
      status: 'all',
      tickets: [],
      client: '',
      clients: [],
      groupName: '',
      groups: [],
      auction: '',
      auctions: [],
      selectedTicket: {},
      statusData: [
        { key: 'all', label: 'All', count: 0 },
        { key: 'IN', label: 'In Progress', count: 0 },
        { key: 'OUT', label: 'Completed', count: 0 },
      ],
      is_loading: false,
      is_exporting: false,
      isGeneratingPDF: false,
      limit: 15,
      page: 1,
      total: 0,
      hideSidebar: false,
      showModal: false,
      showPrintModal: false,
      printTicketData: null,
      // Export modal state
      showExportModal: false,
      exportFilters: {
        start_date: today,
        end_date: today,
        delivery_site_id: '',
        status: 'OUT',
        export_excel: true,
      },
      exportErrors: {},
      // Delivery sites for export filter
      deliverySites: [],
      loadingDeliverySites: false,
      loadingClients: false,
      loadingGroups: false,
      loadingAuction: false,
      sendingReport: false,
    };

    // Bind methods
    this.handlePageChange = this.handlePageChange.bind(this);
    this.handleRowsPerPageChange = this.handleRowsPerPageChange.bind(this);
    this.handleRowClick = this.handleRowClick.bind(this);
    this.handleExport = this.handleExport.bind(this);
    this.handleExportFilterChange = this.handleExportFilterChange.bind(this);
    this.exportToExcel = this.exportToExcel.bind(this);
    this.handlePrintTicket = this.handlePrintTicket.bind(this);
    this.generateAndDownloadPDF = this.generateAndDownloadPDF.bind(this);
    this.fetchDeliverySites = this.fetchDeliverySites.bind(this);
  }

  componentDidMount() {
    window.scrollTo(0, 0);

    // Get today's date in YYYY-MM-DD format
    const today = new Date().toISOString().split('T')[0];

    // Set start date to a month ago to ensure we include all data
    const oneMonthAgo = new Date();
    oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
    const oneMonthAgoStr = oneMonthAgo.toISOString().split('T')[0];

    const params = new URLSearchParams(window.location.search);
    const search = {
      vehicle_id: params.get('vehicle_id') || '',
      delivery_site_id: params.get('delivery_site_id') || '',
      status: params.get('status') || '',
      driver_id: params.get('driver_id') || '',
      ticket_number: params.get('ticket_number') || '',
      date: params.get('date') || today,
    };

    const status = params.get('status') || 'all';
    const page = parseInt(params.get('page')) || 1;
    const limit = parseInt(params.get('limit')) || 15;

    // Initialize export filters with wider date range
    this.setState(
      {
        search,
        status,
        page,
        limit,
        exportFilters: {
          ...this.state.exportFilters,
          start_date: oneMonthAgoStr,
          end_date: today,
        },
      },
      () => {
        this.fetchWeighbridgeTickets();
      }
    );
    this.fetchAllOrganization();
    this.fetchDeliverySites();
  }

  componentWillUnmount() {
    // Clean up any pending timeouts or intervals
    if (this.pdfTimeout) {
      clearTimeout(this.pdfTimeout);
    }
  }

  updateUrlQuery(q) {
    const queryString = new URLSearchParams(
      Object.fromEntries(Object.entries(q).filter(([_, v]) => v !== '' && v !== null))
    ).toString();
    window.history.replaceState(null, '', `${window.location.pathname}?${queryString}`);
  }

  fetchAllOrganization = async () => {
    this.setState({ loadingClients: true });

    try {
      const response = await getOrganizationSelectors();
      if (response.success && response.data) {
        const clients = response.data.map((client) => ({
          id: client.id,
          value: client.id,
          label: client.organization_name,
          organization_name: client.organization_name,
        }));

        this.setState({
          clients,
          loadingClients: false,
        });
      } else {
        toast.error(response.message || 'Failed to fetch organization');
        this.setState({ loadingClients: false });
      }
    } catch (error) {
      console.error('Error fetching organization:', error);
      toast.error('Error loading organization list');
      this.setState({ loadingClients: false });
    }
  };

  fetchGroupsList = async (organizationId) => {
    this.setState({ loadingGroups: true });

    try {
      const filters = {
        organization_id: organizationId,
      };

      const response = await getGroupsSelectors(filters);

      if (response.success && response.data) {
        const groups = response.data.map((group) => ({
          id: group.id,
          value: group.id,
          label: group.group_name,
          name: group.group_name,
          organization_id: group.organization_id,
        }));

        this.setState({
          groups,
          loadingGroups: false,
        });
      } else {
        toast.error(response.message || 'Failed to fetch groups');
        this.setState({ loadingGroups: false, groups: [] });
      }
    } catch (error) {
      console.error('Error fetching groups:', error);
      toast.error('Error loading groups list');
      this.setState({
        loadingGroups: false,
        groups: [],
        groupName: '',
        groupActualName: '',
      });
    }
  };

  fetchDeliverySites = async () => {
    this.setState({ loadingDeliverySites: true });
    try {
      const response = await getDeliverySiteSelectors();
      if (response.success && response.data) {
        const deliverySites = response.data.map((site) => ({
          id: site.value,
          value: site.value,
          label: site.label,
        }));
        this.setState({
          deliverySites,
          loadingDeliverySites: false,
        });
      } else {
        console.error('Failed to fetch delivery sites:', response.message);
        this.setState({
          loadingDeliverySites: false,
          deliverySites: [],
        });
      }
    } catch (error) {
      console.error('Error fetching delivery sites:', error);
      this.setState({
        loadingDeliverySites: false,
        deliverySites: [],
      });
    }
  };

  async fetchWeighbridgeTickets() {
    this.setState({ is_loading: true });
    let { limit, page, status, search } = this.state;

    try {
      const filters = {
        page: page,
        per_page: limit,
      };

      if (status && status !== 'all') {
        filters.status = status;
      }

      if (search.vehicle_id) {
        filters.vehicle_id = search.vehicle_id;
      }

      if (search.delivery_site_id) {
        filters.delivery_site_id = search.delivery_site_id;
      }

      if (search.driver_id) {
        filters.driver_id = search.driver_id;
      }

      if (search.ticket_number) {
        filters.ticket_number = search.ticket_number;
      }

      if (search.date) {
        filters.date = search.date;
      }

      if (search.is_active) {
        filters.is_active = search.is_active;
      }

      const response = await getWeighbridgeTicketList(filters);

      if (response.success) {
        let tickets = response.data.data || [];
        let meta = response.data || {};

        const statusCounts = {
          total: meta.total || 0,
          IN: 0,
          OUT: 0,
          WGH: 0,
        };

        tickets.forEach((ticket) => {
          if (ticket.status && statusCounts[ticket.status] !== undefined) {
            statusCounts[ticket.status]++;
          }
        });

        this.setState({
          tickets: tickets,
          total: meta.total || 0,
          statusData: [
            { key: 'all', label: 'All', count: statusCounts.total || 0 },
            { key: 'IN', label: 'In Progress', count: statusCounts.IN || 0 },
            { key: 'OUT', label: 'Completed', count: statusCounts.OUT || 0 },
          ],
          is_loading: false,
        });
      } else {
        console.error('Failed to fetch weighbridge tickets:', response.message);
        this.setState({
          tickets: [],
          is_loading: false,
        });
      }
    } catch (error) {
      console.error('Error fetching weighbridge tickets:', error);
      this.setState({
        tickets: [],
        is_loading: false,
      });
    }
  }

  handleClientChange = (e) => {
    const clientId = e.target.value;
    const selectedClient = this.state.clients.find((c) => c.id == clientId);

    this.setState((prevState) => ({
      client: clientId,
      groups: [],
      groupName: '',
      groupActualName: '',
      auctions_data: [],
      errors: {
        ...prevState.errors,
        client: '',
        groupName: '',
        groupActualName: '',
      },
    }));

    if (clientId) {
      this.fetchGroupsList(clientId);
      if (selectedClient) {
        toast.success(`Client "${selectedClient.organization_name}" selected. Loading groups...`);
      }
    }
  };

  handleTabChange = (key) => {
    this.setState({ status: key, page: 1 }, () => {
      this.fetchWeighbridgeTickets();
    });
  };

  handlePageChange = (page) => {
    this.setState({ page: page }, () => {
      this.fetchWeighbridgeTickets();
    });
  };

  handleRowsPerPageChange = (newLimit, page) => {
    this.setState({ limit: newLimit, page: 1 }, () => {
      this.fetchWeighbridgeTickets();
    });
  };

  handleRowClick = (row, e) => {
    if (this.props.history && row.id) {
      this.props.history.push(`/weighbridge-ticket/view/${row.id}`);
    }
  };

  handleEdit = (row, e) => {
    e?.stopPropagation();
    if (this.props.history && row.id) {
      this.props.history.push(`/weighbridge-ticket/view/${row.id}`);
    }
  };

  // Print/Download Ticket Function
  handlePrintTicket = async (ticket, autoDownload = false) => {
    // Get material name from the ticket
    const materialName =
      ticket.delivery_site?.material?.name || ticket.materialType || 'No Material Specified';

    const printData = {
      ...ticket,
      gatepass_number: ticket.gatepass_number || '',
      weight_ticket: ticket.weight_ticket || '',
      tare_weight: parseFloat(ticket.tare_weight || 0),
      gross_weight: parseFloat(ticket.gross_weight || 0),
      net_weight: parseFloat(ticket.net_weight || 0),
      entry_time: ticket.entry_time || ticket.checkInTime || '',
      exit_time: ticket.exit_time || ticket.checkOutTime || '',
      ticket_number: ticket.ticket_number,
      yard: ticket.yard || {
        id: ticket.yard_id || '',
        name: ticket.yard?.name || 'Not Specified',
      },
      materialName: materialName,
      delivery_site: {
        ...ticket.delivery_site,
        material: ticket.delivery_site?.material || {
          name: materialName,
        },
      },
      vehicle: ticket.vehicle || {
        vehicle_number: ticket.vehicleNumber || '',
        vehicle_model: ticket.vehicleModel || '',
        trailer_number: ticket.trailerNumber || '',
      },
      driver: ticket.driver || {
        name: ticket.driverName || '',
        license_number: ticket.driverLicense || '',
        phone: ticket.driverContact || '',
      },
    };

    this.setState(
      {
        printTicketData: printData,
        printMaterialName: materialName,
        isGeneratingPDF: true,
      },
      async () => {
        await new Promise((resolve) => setTimeout(resolve, 300));

        try {
          if (!this.printRef.current) {
            throw new Error('Print component not ready. Please try again.');
          }

          await this.generateAndDownloadPDF(autoDownload);

          if (!autoDownload) {
            this.setState({ showPrintModal: true });
          }
        } catch (error) {
          console.error('Error generating PDF:', error);
          toast.error(error.message || 'Failed to generate PDF');
        } finally {
          this.setState({ isGeneratingPDF: false });
        }
      }
    );
  };

  generateAndDownloadPDF = async (autoDownload = false) => {
    if (!this.printRef.current) {
      toast.error('Print reference not found');
      throw new Error('Print reference not found');
    }

    const element = this.printRef.current;
    element.style.display = 'block';
    element.style.visibility = 'visible';

    const options = {
      margin: 10,
      filename: `Weight_Ticket_${this.state.printTicketData?.weight_ticket || this.state.printTicketData?.ticket_number || 'ticket'}.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: {
        scale: 2,
        useCORS: true,
        letterRendering: true,
        allowTaint: true,
        backgroundColor: '#FFFFFF',
      },
      jsPDF: {
        unit: 'mm',
        format: 'a4',
        orientation: 'portrait',
      },
    };

    try {
      if (!autoDownload) {
        toast.info('Generating PDF...');
      }

      await html2pdf().set(options).from(element).save();

      if (autoDownload) {
        toast.success('Weight ticket downloaded automatically!');
      } else {
        toast.success('Weight ticket downloaded!');
      }

      return true;
    } catch (error) {
      console.error('PDF generation error:', error);

      try {
        const printWindow = window.open('', '_blank');
        printWindow.document.write(`
                <html>
                    <head>
                        <title>Weight Ticket - ${this.state.printTicketData?.ticket_number || 'ticket'}</title>
                        <style>
                            body { font-family: Arial, sans-serif; padding: 20px; }
                            @media print {
                                body { margin: 0; padding: 0; }
                            }
                        </style>
                    </head>
                    <body>
                        ${element.innerHTML}
                        <script>
                            setTimeout(() => {
                                window.print();
                                setTimeout(() => window.close(), 1000);
                            }, 500);
                        </script>
                    </body>
                </html>
                `);
        printWindow.document.close();

        toast.info('Opening print dialog...');
      } catch (printError) {
        console.error('Print fallback error:', printError);
        toast.error('Please try the print button instead');
      }

      throw error;
    }
  };

  // Export Functions
  handleExport = () => {
    this.setState({ showExportModal: true, exportErrors: {} });
  };

  handleReportSend = async () => {
    try {
      this.setState({ sendingReport: true });

      const response = await sendTransitReport();

      if (response.success) {
        toast.success('Transit report sent successfully!');
      } else {
        toast.error(response.message || 'Failed to send transit report');
      }
    } catch (error) {
      toast.error('Something went wrong while sending the report');
    } finally {
      this.setState({ sendingReport: false });
    }
  };

  handleExportFilterChange = (field, value) => {
    this.setState((prevState) => {
      const newFilters = {
        ...prevState.exportFilters,
        [field]: value,
      };
      const newErrors = { ...prevState.exportErrors };
      const today = new Date().toISOString().split('T')[0];

      // Auto-correct start date logic
      if (field === 'start_date') {
        // If new start_date is after end_date, push end_date to match
        if (newFilters.end_date && value && value > newFilters.end_date) {
          newFilters.end_date = value;
        }
        delete newErrors.start_date;
        delete newErrors.end_date;
      }

      // Validate end date
      if (field === 'end_date') {
        if (newFilters.start_date && value && value < newFilters.start_date) {
          newErrors.end_date = 'End date cannot be before start date';
        } else {
          delete newErrors.end_date;
        }
      }

      // Prevent future dates
      if ((field === 'start_date' || field === 'end_date') && value && value > today) {
        newErrors[field] = 'Date cannot be in the future';
      }

      return {
        exportFilters: newFilters,
        exportErrors: newErrors,
      };
    });
  };

  exportToExcel = async () => {
    const { exportFilters } = this.state;

    // Validation
    const errors = {};
    if (!exportFilters.start_date) errors.start_date = 'Start date is required';
    if (!exportFilters.end_date) errors.end_date = 'End date is required';

    if (exportFilters.start_date && exportFilters.end_date) {
      if (new Date(exportFilters.start_date) > new Date(exportFilters.end_date)) {
        errors.end_date = 'End date must be after start date';
      }

      // Warn if range exceeds 1 year
      const daysDiff =
        (new Date(exportFilters.end_date) - new Date(exportFilters.start_date)) /
        (1000 * 60 * 60 * 24);
      if (daysDiff > 365) {
        toast.warning('Date range exceeds 1 year. Export may take longer.');
      }
    }

    if (Object.keys(errors).length > 0) {
      this.setState({ exportErrors: errors });
      toast.error('Please fix the errors in the form');
      return;
    }

    this.setState({ is_exporting: true, exportErrors: {} });

    try {
      // Only send non-empty filters
      const cleanFilters = Object.fromEntries(
        Object.entries(exportFilters).filter(([_, v]) => v !== '' && v !== null && v !== undefined)
      );

      const response = await exportWeighbridgeTickets(cleanFilters);

      if (response.data) {
        if (response.data instanceof Blob) {
          const url = window.URL.createObjectURL(response.data);
          const link = document.createElement('a');
          link.href = url;
          const filename = `DAILY_COLLECTION_REPORT_${exportFilters.start_date}_TO_${exportFilters.end_date}.xlsx`;
          link.setAttribute('download', filename);
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);
          toast.success('Report downloaded successfully');
        } else {
          this.generateExcelFromData(response.data);
        }
        this.setState({ showExportModal: false });
      } else {
        console.error('Export failed:', response.message);
        toast.error('Failed to export report: ' + (response.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error exporting report:', error);
      toast.error('Error exporting report: ' + error.message);
    } finally {
      this.setState({ is_exporting: false });
    }
  };

  generateExcelFromData = (data) => {
    if (data.summary) {
      toast.success(
        `Report generated successfully!\n\nTotal Tickets: ${data.summary.total_tickets}\nTotal Net Weight: ${data.summary.total_net_weight_mt} MT`
      );
    }
  };

  getStatusBadge = (status) => {
    const statusMap = {
      IN: { label: 'In Progress', color: '#3b82f6', bgColor: '#dbeafe' },
      OUT: { label: 'Completed', color: '#10b981', bgColor: '#d1fae5' },
    };

    const statusInfo = statusMap[status] || { label: status, color: '#6b7280', bgColor: '#f3f4f6' };

    return (
      <span
        style={{
          backgroundColor: statusInfo.bgColor,
          color: statusInfo.color,
          padding: '4px 12px',
          borderRadius: '20px',
          fontSize: '12px',
          fontWeight: '500',
          whiteSpace: 'nowrap',
          display: 'inline-flex',
          alignItems: 'center',
          gap: '4px',
        }}
      >
        <span
          style={{
            width: '6px',
            height: '6px',
            borderRadius: '50%',
            backgroundColor: statusInfo.color,
          }}
        />
        {statusInfo.label}
      </span>
    );
  };

  formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  formatTime = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  formatWeight = (weight) => {
    return parseFloat(weight || 0).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  getColumns() {
    return [
      {
        name: '#',
        selector: (row, index) => index + 1,
        width: '60px',
        center: true,
      },
      {
        name: 'Download',
        center: true,
        cell: (row) => (
          <div className="d-flex align-items-center justify-content-center gap-2">
            <button
              className="button1 button1-primary"
              onClick={(e) => {
                e.stopPropagation();
                this.handlePrintTicket(row, true);
              }}
              disabled={this.state.isGeneratingPDF}
              style={{ padding: '6px 10px', fontSize: '12px' }}
              title="Download Ticket"
            >
              {this.state.isGeneratingPDF ? (
                <span
                  className="spinner-border spinner-border-sm"
                  role="status"
                  aria-hidden="true"
                ></span>
              ) : (
                <Download size={14} />
              )}
            </button>
          </div>
        ),
      },
      {
        name: 'Ticket Number',
        selector: (row) => row.ticket_number,
        sortable: true,
        width: '150px',
        cell: (row) => (
          <Link to={`/weighbridge-ticket/view/${row.id}`} onClick={(e) => this.handleEdit(row, e)}>
            <div style={{ fontWeight: '600', color: '#1e40af' }}>{row.ticket_number}</div>
            <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '2px' }}>
              {this.formatDate(row.created_at)}
            </div>
          </Link>
        ),
      },
      {
        name: 'Vehicle',
        selector: (row) => row.vehicle?.vehicle_number || '-',
        sortable: true,
        width: '120px',
        cell: (row) => (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Truck size={14} color="#6b7280" />
            <div style={{ fontSize: '13px', fontWeight: '500' }}>
              {row.vehicle?.vehicle_number || '-'}
            </div>
          </div>
        ),
      },
      {
        name: 'Driver',
        selector: (row) => row.driver?.name || '-',
        sortable: true,
        width: '150px',
        cell: (row) => (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <User size={14} color="#6b7280" />
            <span style={{ fontSize: '13px' }}>{row.driver?.name || '-'}</span>
          </div>
        ),
      },
      {
        name: 'Delivery Site',
        selector: (row) => row.delivery_site?.auction_name || '-',
        sortable: true,
        width: '180px',
        cell: (row) => (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <MapPin size={14} color="#6b7280" />
            <div>
              <div style={{ fontSize: '13px', fontWeight: '500' }}>
                {row.delivery_site?.auction_name || '-'}
              </div>
              {row.delivery_site?.progress !== undefined && (
                <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '2px' }}>
                  Progress: {parseFloat(row.delivery_site?.progress || 0).toFixed(1)}%
                </div>
              )}
            </div>
          </div>
        ),
      },
      {
        name: 'Weight Details',
        width: '180px',
        cell: (row) => {
          const tareWeight = parseFloat(row.tare_weight) || 0;
          const grossWeight = parseFloat(row.gross_weight) || 0;
          const netWeight = parseFloat(row.net_weight) || 0;

          return (
            <div className="weight-details" style={{ fontSize: '12px' }}>
              <div
                style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}
              >
                <span style={{ color: '#6b7280' }}>Tare:</span>
                <span style={{ fontWeight: '600' }}>{this.formatWeight(tareWeight)} kg</span>
              </div>
              <div
                style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}
              >
                <span style={{ color: '#6b7280' }}>Gross:</span>
                <span style={{ fontWeight: '600', color: grossWeight > 0 ? '#3b82f6' : '#6b7280' }}>
                  {grossWeight > 0 ? this.formatWeight(grossWeight) + ' kg' : '-'}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: '#6b7280' }}>Net:</span>
                <span style={{ fontWeight: '600', color: netWeight > 0 ? '#10b981' : '#6b7280' }}>
                  {netWeight > 0 ? this.formatWeight(netWeight) + ' kg' : '-'}
                </span>
              </div>
            </div>
          );
        },
      },
      {
        name: 'Yard',
        cell: (row) => (
          <div style={{ fontSize: '12px' }}>
            {row.yard ? (
              <div>
                <div style={{ fontWeight: '500', color: '#1e40af' }}>{row.yard.name}</div>
                <div style={{ fontSize: '11px', color: '#6b7280' }}>
                  Loaded: {this.formatWeight(row.yard.loaded_weight)} kg
                </div>
              </div>
            ) : (
              <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>No yard</span>
            )}
          </div>
        ),
      },
      {
        name: 'Timing',
        cell: (row) => (
          <div style={{ fontSize: '12px' }}>
            <div style={{ marginBottom: '4px' }}>
              <div style={{ fontSize: '10px', color: '#6b7280', marginBottom: '2px' }}>
                Entry:
                <span style={{ fontWeight: '500' }}>
                  {row.entry_time ? this.formatTime(row.entry_time) : '-'}
                </span>
              </div>
            </div>
            <div>
              <div style={{ fontSize: '10px', color: '#6b7280', marginBottom: '2px' }}>
                Exit:
                <span style={{ fontWeight: '500', color: row.exit_time ? '#10b981' : '#6b7280' }}>
                  {row.exit_time ? this.formatTime(row.exit_time) : '-'}
                </span>
              </div>
            </div>
            {row.entry_time && row.exit_time && (
              <div style={{ fontSize: '10px', color: '#6b7280', marginTop: '4px' }}>
                Duration:{' '}
                {row.entry_time && row.exit_time
                  ? Math.round((new Date(row.exit_time) - new Date(row.entry_time)) / (1000 * 60)) +
                    ' min'
                  : '-'}
              </div>
            )}
          </div>
        ),
      },
      {
        name: 'Status',
        selector: (row) => row.status,
        sortable: true,
        width: '120px',
        cell: (row) => this.getStatusBadge(row.status),
      },
    ];
  }

  handleSearchChange = (field, value) => {
    this.setState((prevState) => ({
      search: {
        ...prevState.search,
        [field]: value,
      },
    }));
  };

  handleSearchSubmit = () => {
    this.setState({ page: 1 }, () => {
      this.fetchWeighbridgeTickets();
    });
  };

  handleGroupChange = (e) => {
    const groupId = e.target.value;
    const selectedGroup = this.state.groups.find((g) => g.id == groupId);

    this.setState((prevState) => ({
      groupName: groupId,
      groupActualName: selectedGroup ? selectedGroup.label : '',
      errors: {
        ...prevState.errors,
        groupName: '',
        groupActualName: '',
      },
      auctions_data: [],
    }));

    if (groupId) {
      this.fetchAuctionList(groupId);
      if (selectedGroup) {
        toast.success(`Group "${selectedGroup.label}" selected. Loading auctions...`);
      }
    }
  };

  handleClearFilters = () => {
    const today = new Date().toISOString().split('T')[0];

    this.setState(
      {
        search: {
          vehicle_id: '',
          delivery_site_id: '',
          status: '',
          driver_id: '',
          ticket_number: '',
          date: '',
        },
        status: 'all',
        page: 1,
      },
      () => {
        this.fetchWeighbridgeTickets();
      }
    );
  };

  ExportModal = () => {
    const {
      showExportModal,
      exportFilters,
      is_exporting,
      deliverySites,
      loadingDeliverySites,
      exportErrors,
    } = this.state;

    if (!showExportModal) return null;

    const today = new Date().toISOString().split('T')[0];
    const hasErrors = Object.keys(exportErrors).length > 0;

    return (
      <div
        className="modal-overlay"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
        }}
      >
        <div
          className="modal-content"
          style={{
            backgroundColor: 'white',
            padding: '30px',
            borderRadius: '12px',
            maxWidth: '500px',
            width: '90%',
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)',
            maxHeight: '90vh',
            overflowY: 'auto',
          }}
        >
          <div
            className="modal-header"
            style={{
              marginBottom: '20px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <h3 style={{ margin: 0, color: '#1e40af' }}>
              <FileSpreadsheet size={20} style={{ marginRight: '10px', verticalAlign: 'middle' }} />
              Export Daily Collection Report
            </h3>
            <button
              onClick={() => this.setState({ showExportModal: false, exportErrors: {} })}
              style={{
                background: 'none',
                border: 'none',
                fontSize: '24px',
                cursor: 'pointer',
                color: '#6b7280',
              }}
            >
              &times;
            </button>
          </div>

          <div className="modal-body">
            {/* Date Range */}
            <div style={{ marginBottom: '20px' }}>
              <label
                style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontWeight: '500',
                  color: '#374151',
                }}
              >
                <Calendar size={16} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                Date Range <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <div style={{ display: 'flex', gap: '15px', alignItems: 'flex-start' }}>
                <div style={{ flex: 1 }}>
                  <input
                    type="date"
                    value={exportFilters.start_date}
                    onChange={(e) => this.handleExportFilterChange('start_date', e.target.value)}
                    max={today}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: `1px solid ${exportErrors.start_date ? '#ef4444' : '#d1d5db'}`,
                      borderRadius: '8px',
                      fontSize: '14px',
                    }}
                  />
                  <div
                    style={{
                      fontSize: '12px',
                      color: exportErrors.start_date ? '#ef4444' : '#6b7280',
                      marginTop: '4px',
                    }}
                  >
                    {exportErrors.start_date || 'Start Date'}
                  </div>
                </div>
                <span style={{ color: '#6b7280', fontWeight: '500', paddingTop: '10px' }}>to</span>
                <div style={{ flex: 1 }}>
                  <input
                    type="date"
                    value={exportFilters.end_date}
                    onChange={(e) => this.handleExportFilterChange('end_date', e.target.value)}
                    min={exportFilters.start_date || undefined}
                    max={today}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: `1px solid ${exportErrors.end_date ? '#ef4444' : '#d1d5db'}`,
                      borderRadius: '8px',
                      fontSize: '14px',
                    }}
                  />
                  <div
                    style={{
                      fontSize: '12px',
                      color: exportErrors.end_date ? '#ef4444' : '#6b7280',
                      marginTop: '4px',
                    }}
                  >
                    {exportErrors.end_date || 'End Date'}
                  </div>
                </div>
              </div>
            </div>

            {/* Delivery Site Filter */}
            <div style={{ marginBottom: '20px' }}>
              <label
                style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontWeight: '500',
                  color: '#374151',
                }}
              >
                <MapPin size={16} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                Delivery Site
              </label>
              <select
                value={exportFilters.delivery_site_id}
                onChange={(e) => this.handleExportFilterChange('delivery_site_id', e.target.value)}
                disabled={loadingDeliverySites}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '14px',
                  backgroundColor: 'white',
                }}
              >
                <option value="">All Delivery Sites</option>
                {loadingDeliverySites ? (
                  <option value="" disabled>
                    Loading delivery sites...
                  </option>
                ) : deliverySites.length === 0 ? (
                  <option value="" disabled>
                    No delivery sites available
                  </option>
                ) : (
                  deliverySites.map((site) => (
                    <option key={site.id} value={site.id}>
                      {site.label}
                    </option>
                  ))
                )}
              </select>
              <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                Leave empty to export data from all delivery sites
              </div>
            </div>

            {/* Status Filter */}
            <div style={{ marginBottom: '20px' }}>
              <label
                style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontWeight: '500',
                  color: '#374151',
                }}
              >
                Ticket Status
              </label>
              <select
                value={exportFilters.status}
                onChange={(e) => this.handleExportFilterChange('status', e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '14px',
                  backgroundColor: 'white',
                }}
              >
                <option value="OUT">Completed Tickets Only</option>
                <option value="IN">In Progress Only</option>
                <option value="all">All Statuses</option>
              </select>
            </div>

            <div
              style={{
                marginBottom: '25px',
                padding: '15px',
                backgroundColor: '#f0f9ff',
                borderRadius: '8px',
                border: '1px solid #dbeafe',
              }}
            >
              <div
                style={{
                  fontSize: '13px',
                  color: '#1e40af',
                  fontWeight: '500',
                  marginBottom: '5px',
                }}
              >
                <InfoIcon size={14} style={{ marginRight: '5px', verticalAlign: 'middle' }} />
                Report Format
              </div>
              <div style={{ fontSize: '12px', color: '#4b5563' }}>
                The exported report will be in Excel format (.xlsx) matching the Daily Collection
                Report template.
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '15px' }}>
              <button
                onClick={() => this.setState({ showExportModal: false, exportErrors: {} })}
                disabled={is_exporting}
                className="button1 button1-gray"
              >
                Cancel
              </button>
              <button
                onClick={this.exportToExcel}
                disabled={is_exporting || hasErrors}
                className="button1 button1-primary"
              >
                {is_exporting ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm"
                      role="status"
                      aria-hidden="true"
                    ></span>
                    Exporting...
                  </>
                ) : (
                  <>
                    <Download size={16} />
                    Export Report
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  render() {
    const {
      tickets,
      is_loading,
      statusData,
      status,
      page,
      limit,
      total,
      hideSidebar,
      client,
      clients,
      loadingClients,
      groups,
      loadingGroups,
      groupName,
      auction,
      auctions,
      loadingAuction,
      showPrintModal,
      printTicketData,
      isGeneratingPDF,
    } = this.state;
    const shouldShowPrintComponent = printTicketData;

    const customTableStyles = {
      ...tableStyles,
      rows: {
        ...tableStyles.rows,
        style: {
          ...tableStyles.rows?.style,
          cursor: 'pointer',
          '&:hover': {
            backgroundColor: '#f8f9fa',
          },
        },
      },
    };

    return (
      <React.Fragment>
        {/* Hidden Print Component */}
        <div style={{ display: 'none', position: 'absolute', left: '-9999px' }}>
          {shouldShowPrintComponent && (
            <ForwardedWeightTicketPrint
              ref={this.printRef}
              ticket={printTicketData}
              material={
                printTicketData?.materialName ||
                printTicketData?.delivery_site?.material?.name ||
                ''
              }
              driver={printTicketData?.driver || {}}
              yard={printTicketData?.yard || {}}
              vehicle={printTicketData?.vehicle || {}}
              deliverySite={printTicketData?.delivery_site || {}}
            />
          )}
        </div>

        <HeaderBanner
          title={i18n.t('weighbridge_tickets')}
          subtitle={i18n.t('manage_weighbridge_tickets')}
        />
        <section className="mt-5">
          <div className="container">
            <div className="row">
              <div className="col-xl-3 col-lg-12 col-md-12">{!hideSidebar && <SideBar />}</div>
              <div
                className={
                  hideSidebar ? 'col-xl-12 col-lg-12 col-md-12' : 'col-xl-9 col-lg-12 col-md-12'
                }
              >
                <div className="card">
                  <div className="card-header">
                    <div className="expand-dashboard">
                      <span
                        style={{ fontSize: '1rem', marginRight: '0.5rem', cursor: 'pointer' }}
                        onClick={() => {
                          this.setState({
                            hideSidebar: !hideSidebar,
                          });
                        }}
                      >
                        {hideSidebar && <i className="icon-size-actual"></i>}
                        {!hideSidebar && <i className="icon-size-fullscreen"></i>}
                      </span>
                    </div>
                    <div>
                      <button
                        onClick={this.handleReportSend}
                        className="button1 button1-success"
                        disabled={this.state.sendingReport}
                        style={{ float: 'right', marginLeft: '10px' }}
                      >
                        {this.state.sendingReport ? (
                          <>
                            <span
                              className="spinner-border spinner-border-sm"
                              role="status"
                              aria-hidden="true"
                              style={{ marginRight: '8px' }}
                            ></span>
                            Sending...
                          </>
                        ) : (
                          <>
                            <Mail size={16} style={{ marginRight: '8px' }} />
                            Send Report
                          </>
                        )}
                      </button>

                      <button
                        onClick={this.handleExport}
                        className="button1 button1-success"
                        style={{ float: 'right', marginLeft: '10px' }}
                      >
                        <Download size={16} style={{ marginRight: '8px' }} />
                        Export Report
                      </button>
                    </div>
                  </div>
                  <div className="card-body">
                    <FilterSection
                      loading={this.state.isLoading}
                      onSearch={this.handleSearchSubmit}
                      onClear={this.handleClearFilters}
                    >
                      <div className="select-wrapper floating-label-wrapper">
                        <select
                          value={client}
                          onChange={this.handleClientChange}
                          disabled={loadingClients}
                          className="filter-base floating-input"
                        >
                          <option value="">Select Client</option>
                          {loadingClients ? (
                            <option value="" disabled>
                              Loading Clients...
                            </option>
                          ) : (
                            clients.map((client) => (
                              <option key={client.id} value={client.id}>
                                {client.label}
                              </option>
                            ))
                          )}
                        </select>
                        <label className="floating-label">{i18n.t('organization')}</label>
                      </div>

                      <div className="select-wrapper floating-label-wrapper">
                        <select
                          value={groupName}
                          onChange={this.handleGroupChange}
                          className="filter-base floating-input"
                          disabled={loadingGroups || !client}
                        >
                          <option value="">Select Group</option>
                          {!client ? (
                            <option value="" disabled>
                              Select a client first
                            </option>
                          ) : loadingGroups ? (
                            <option value="" disabled>
                              Loading Groups...
                            </option>
                          ) : groups.length === 0 ? (
                            <option value="" disabled>
                              No groups found for this client
                            </option>
                          ) : (
                            groups.map((group) => (
                              <option key={group.id} value={group.id}>
                                {group.label}
                              </option>
                            ))
                          )}
                        </select>
                        <label className="floating-label">{i18n.t('project_name')}</label>
                      </div>

                      <div className="select-wrapper floating-label-wrapper">
                        <select
                          id={`auction-select`}
                          value={auction}
                          className="filter-base floating-input"
                          onChange={(e) => this.handleAuctionChange(e.target.value)}
                          disabled={loadingAuction || !groupName}
                        >
                          <option value="">Select Auction</option>
                          {loadingAuction ? (
                            <option value="" disabled>
                              Loading auctions...
                            </option>
                          ) : !groupName ? (
                            <option value="" disabled>
                              Select a group first
                            </option>
                          ) : auctions.length === 0 ? (
                            <option value="" disabled>
                              No auctions found for this group
                            </option>
                          ) : (
                            auctions.map((auction) => (
                              <option key={auction.id} value={auction.id}>
                                {auction.label}
                              </option>
                            ))
                          )}
                        </select>
                        <label className="floating-label">{i18n.t('auction_name')}</label>
                      </div>

                      <div className="select-wrapper floating-label-wrapper">
                        <input
                          type="text"
                          className="filter-base floating-input"
                          value={this.state.search.vehicle_id}
                          onChange={(e) => this.handleSearchChange('vehicle_id', e.target.value)}
                        />
                        <label className="floating-label">Vehicle</label>
                      </div>

                      <div className="select-wrapper floating-label-wrapper">
                        <input
                          type="date"
                          className="filter-base floating-input"
                          value={this.state.search.date}
                          onChange={(e) => this.handleSearchChange('date', e.target.value)}
                          max={new Date().toISOString().split('T')[0]}
                        />
                        <label className="floating-label">Date</label>
                      </div>
                    </FilterSection>

                    <StatusTabs
                      tabs={statusData}
                      onTabChange={this.handleTabChange}
                      selected={status}
                    />

                    <div className="Data-Listing">
                      {is_loading ? (
                        <TableSkeleton columns={10} rows={10} />
                      ) : tickets.length ? (
                        <DataTable
                          key={`${page}-${limit}`}
                          title=""
                          columns={this.getColumns()}
                          data={tickets}
                          customStyles={customTableStyles}
                          highlightOnHover
                          pagination
                          paginationServer
                          paginationTotalRows={total}
                          paginationPerPage={limit}
                          paginationDefaultPage={page}
                          paginationRowsPerPageOptions={[15, 25, 50, 100]}
                          paginationComponentOptions={{
                            rowsPerPageText: i18n.t('rows_per_page'),
                            rangeSeparatorText: i18n.t('out'),
                          }}
                          onChangeRowsPerPage={this.handleRowsPerPageChange}
                          onChangePage={this.handlePageChange}
                          onRowClicked={this.handleRowClick}
                        />
                      ) : (
                        <NoData
                          message="No weighbridge tickets available"
                          actionText="Refresh"
                          onAction={this.fetchWeighbridgeTickets.bind(this)}
                        />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Print Modal */}
        {showPrintModal && printTicketData && (
          <div className="modal-overlay">
            <div className="modal-content print-modal" style={{ maxWidth: '800px', width: '90%' }}>
              <div className="modal-header">
                <h3>
                  <Printer size={20} /> Weight Ticket Preview - {printTicketData?.ticket_number}
                </h3>
                <button onClick={() => this.setState({ showPrintModal: false })}>×</button>
              </div>
              <div className="modal-body">
                <div
                  className="print-preview-container"
                  style={{ maxHeight: '70vh', overflow: 'auto' }}
                >
                  <ForwardedWeightTicketPrint
                    ref={this.printRef}
                    ticket={printTicketData}
                    material={
                      printTicketData?.materialName ||
                      printTicketData?.delivery_site?.material?.name ||
                      ''
                    }
                    driver={printTicketData?.driver || {}}
                    yard={printTicketData?.yard || {}}
                    vehicle={printTicketData?.vehicle || {}}
                    deliverySite={printTicketData?.delivery_site || {}}
                  />
                </div>

                <div
                  className="print-actions"
                  style={{
                    display: 'flex',
                    gap: '10px',
                    justifyContent: 'center',
                    marginTop: '20px',
                  }}
                >
                  <button
                    className="button1 button1-primary"
                    onClick={() => this.generateAndDownloadPDF(true)}
                    disabled={isGeneratingPDF}
                  >
                    {isGeneratingPDF ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-hidden="true"
                        ></span>
                        Downloading...
                      </>
                    ) : (
                      <>
                        <Download size={16} /> Download PDF
                      </>
                    )}
                  </button>
                  <button className="button1 button1-secondary" onClick={() => window.print()}>
                    <Printer size={16} /> Print
                  </button>
                  <button
                    className="button1 button1-outline"
                    onClick={() => this.setState({ showPrintModal: false })}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Export Modal */}
        {this.ExportModal()}
      </React.Fragment>
    );
  }
}

export default WeighbridgeTicketList;
